import type { ReportSettings } from './report'

/**
 * Whether a report goes out on a given day, and where that leaves it in the delivery queue.
 *
 * The rule, per Kaushik 2026-09-18: **the schedule says whether a report is due, not whether
 * anyone has started it.** A report configured for weekdays at 08:00 is due every weekday whether
 * or not a draft exists — a draft nobody has created yet is precisely the work the queue is
 * supposed to surface. What takes a report off the list is a SENT issue for that day.
 *
 * "That day" is evaluated in the REPORT'S timezone, because that is where its schedule is
 * written: "Mon–Fri 08:00" means 08:00 where the client reads it, not where the associate sits.
 * An associate in London looking at a New York report sees it under New York's calendar, which is
 * the same calendar the recipient is on.
 *
 * Pure, and shared: the API buckets the queue with it and the SPA labels rows with it, so the two
 * can never disagree about what "due" means.
 */

type Delivery = ReportSettings['delivery']

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6]
const WEEKDAYS = [1, 2, 3, 4, 5]

/**
 * The days a report sends, as JS day numbers (0 = Sunday).
 *
 * `sendDays` wins when it is set; otherwise the named schedule decides. `weekly` with no days
 * named sends on no day at all rather than every day — a weekly report that never picked its day
 * is misconfigured, and showing it as due seven times a week would be worse than showing it never.
 */
export function scheduledDays(delivery: Delivery): number[] {
  if (delivery.sendDays.length > 0) return [...new Set(delivery.sendDays)].sort()
  if (delivery.schedule === 'daily') return ALL_DAYS
  if (delivery.schedule === 'weekdays') return WEEKDAYS
  return []
}

/** The calendar date in a zone, as `YYYY-MM-DD` — the same shape `report_issues.issueDate` holds. */
export function dateInZone(zone: string, at: Date = new Date()): string {
  try {
    // `en-CA` formats as YYYY-MM-DD, which is the one locale that matches the stored shape.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(at)
  } catch {
    return at.toISOString().slice(0, 10)
  }
}

/** The day of the week in a zone, 0 = Sunday — what `scheduledDays` is compared against. */
export function weekdayInZone(zone: string, at: Date = new Date()): number {
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  try {
    const label = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short' }).format(at)
    const index = names.indexOf(label)
    return index < 0 ? at.getDay() : index
  } catch {
    return at.getDay()
  }
}

/** Whether this report is scheduled to go out on the day `at` falls on, in its own zone. */
export function sendsOn(delivery: Delivery, at: Date = new Date()): boolean {
  return scheduledDays(delivery).includes(weekdayInZone(delivery.timezone, at))
}

/** Where a report sits in today's queue. `null` means it isn't part of today at all. */
export type DeliveryBucket = 'due' | 'scheduled' | 'completed' | 'failed'

/** The status of the report's issue for the day in question, if one exists. */
export type TodayIssueStatus = 'draft' | 'scheduled' | 'sent' | 'failed' | 'skipped'

/**
 * Which bucket a report falls in today.
 *
 * Order matters: sent first, because a report that has gone out is done regardless of what its
 * schedule says; then failed, because a send somebody scheduled did not happen and a person has
 * to decide what to do about it — also regardless of the schedule; then scheduled, because a
 * clock is already running on it; then due, which is every other report the schedule names —
 * including the ones nobody has opened.
 */
export function deliveryBucket(
  delivery: Delivery,
  todayIssue: TodayIssueStatus | null,
  at: Date = new Date(),
): DeliveryBucket | null {
  if (todayIssue === 'sent') return 'completed'
  if (todayIssue === 'failed') return 'failed'
  // Somebody decided today's send is not happening: not owed, not done, not broken.
  if (todayIssue === 'skipped') return null
  if (!sendsOn(delivery, at)) return null
  if (todayIssue === 'scheduled') return 'scheduled'
  return 'due'
}

/**
 * Whether a report belongs in the delivery queue at all: active, and delivered by Headline.
 *
 * The queue is Headline's own day's work (Kaushik, 2026-10-14, CLI-854). A report the legacy
 * portal still delivers (`delivery.legacyDelivery`) is not, and counting it made "Due today" read
 * 262 on a day the portal had sent all of them — Headline never sends those, so they never left
 * Due. A paused report is promised to nobody today. Both stay findable in All reports.
 */
export function inDeliveryQueue(report: { status: string; delivery: Delivery }): boolean {
  return report.status === 'active' && !report.delivery.legacyDelivery
}

/**
 * Whether Headline prepares this report's drafts ahead of its send moments (CLI-982): a manual
 * report in the delivery queue. An analyst still builds and sends every one of them; what changes
 * is that the draft for each scheduled moment is already created and pulled when they open it,
 * the report's lead before the moment (`preloadMinutesOf`), because a pull takes a minute or two
 * and the editing is where their time goes (Phoebe, 2026-10-23). An automatic report needs none of this — the tick
 * builds and sends it — and a report with no scheduled day has no moment to prepare for.
 *
 * One predicate for the tick that preloads and for New draft, which aims a draft at the moment the
 * tick would preload so the two meet on one row instead of twinning.
 */
export function preloadsDrafts(report: { status: string; delivery: Delivery }): boolean {
  return inDeliveryQueue(report) && report.delivery.sendMode === 'manual'
}

interface WallClock {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

function wallClockInZone(date: Date, zone: string): WallClock | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(date)
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value)
    const wall = {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour: get('hour'),
      minute: get('minute'),
    }
    return Object.values(wall).some(Number.isNaN) ? null : wall
  } catch {
    return null
  }
}

/** The instant a wall-clock time occurs in a zone: guess as UTC, then correct by the offset seen there. */
function instantInZone(wall: WallClock, zone: string): Date | null {
  const guess = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute)
  const seen = wallClockInZone(new Date(guess), zone)
  if (!seen) return null
  const seenAsUtc = Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute)
  return new Date(guess - (seenAsUtc - guess))
}

/** How far ahead `upcomingSendTimes` will look: a weekly report's next send is at most 7 days out. */
const HORIZON_DAYS = 9

/**
 * Every moment this report is configured to send in the window (from, to]: each scheduled day's
 * send times, written in the report's zone, as instants — so "06:00 ET on weekdays" comes back
 * as the right UTC instant on either side of a daylight-saving change.
 *
 * Shared on purpose (CLI-741): the tick asks for the next hour to know which automatic issues to
 * create, and the schedule dialog asks for the first moment after now. One function, so the two
 * can never disagree about when a report goes out. The window is capped at HORIZON_DAYS from
 * `from`; a schedule with no days or no times has no moments at all.
 */
export function upcomingSendTimes(delivery: Delivery, from: Date, to: Date): Date[] {
  const days = scheduledDays(delivery)
  if (days.length === 0 || delivery.sendTimes.length === 0 || to <= from) return []
  const start = wallClockInZone(from, delivery.timezone)
  if (!start) return []
  // Multiple sends per day: each extra time is its own moment, on the same days.
  const configured = delivery.multiSend.enabled
    ? [...delivery.sendTimes, ...delivery.multiSend.additionalTimes]
    : delivery.sendTimes
  const times = [...new Set(configured)]
    .map((hhmm) => hhmm.split(':').map(Number))
    .filter(
      (parts): parts is [number, number] => parts.length === 2 && parts.every(Number.isFinite),
    )
  const out: Date[] = []
  for (let offset = 0; offset <= HORIZON_DAYS; offset++) {
    // Noon UTC on the calendar day, so adding days never lands on the wrong date.
    const day = new Date(Date.UTC(start.year, start.month - 1, start.day + offset, 12))
    for (const [hour, minute] of times) {
      const at = instantInZone(
        {
          year: day.getUTCFullYear(),
          month: day.getUTCMonth() + 1,
          day: day.getUTCDate(),
          hour,
          minute,
        },
        delivery.timezone,
      )
      if (!at || at <= from || at > to) continue
      if (!days.includes(weekdayInZone(delivery.timezone, at))) continue
      out.push(at)
    }
  }
  return out.sort((a, b) => a.getTime() - b.getTime())
}

/**
 * The configured moment before `moment` (within HORIZON_DAYS), or null — what the issues made
 * "for" `moment` were made after (CLI-982: the tick yields to a draft somebody made for it by hand).
 */
export function previousSendTime(delivery: Delivery, moment: Date): Date | null {
  const from = new Date(moment.getTime() - HORIZON_DAYS * 86_400_000)
  return upcomingSendTimes(delivery, from, new Date(moment.getTime() - 1)).at(-1) ?? null
}

/** A schedule picker's default: the first configured moment comfortably after now, or null. */
export function nextSendTime(delivery: Delivery, now: Date = new Date()): Date | null {
  const grace = 5 * 60 * 1000
  const from = new Date(now.getTime() + grace)
  return (
    upcomingSendTimes(delivery, from, new Date(from.getTime() + HORIZON_DAYS * 86_400_000))[0] ??
    null
  )
}

/**
 * How long one header's data API request may take, and the day-split fallback that follows one
 * that failed (CLI-994) — the numbers the lead times below are derived from, so a change to either
 * moves the lead with it.
 */
export const TOPIC_PULL_TIMING = {
  /**
   * One data API request: the client aborts here (apps/api lib/data-api.ts). The data API's own
   * statement timeout is 180 s, so a request given up on may still be running there.
   */
  REQUEST_SECONDS: 90,
  /**
   * A request over more than a day that timed out, or that the data API failed, is asked again in
   * pieces, all at once: one per day up to this many days (a weekend and its day of lookback),
   * equal shares of a longer window beyond it. Each piece is one more query at the data API.
   */
  SPLIT_MAX_PIECES: 4,
} as const

/**
 * The longest one header's request runs where the fallback runs inline — the system's builds: the
 * request that failed, then its pieces side by side. The editor's Pull never waits this long in
 * one request; it reports the header and asks for the pieces in a request of their own, because
 * the public hostname's proxy gives up on a response after 125 seconds.
 */
export const HEADER_PULL_WORST_SECONDS = TOPIC_PULL_TIMING.REQUEST_SECONDS * 2

const GENERATE_ATTEMPTS = 3
const GENERATE_RETRY_MINUTES = 5
const TICK_MINUTES = 5
/** The longest pull planned for: "at most 5 minutes, usually instant". Cloud Run allows 15. */
const PULL_BUDGET_MINUTES = 5
/**
 * How long before the moment generation starts, derived (CLI-994). QStash waits
 * GENERATE_RETRY_MINUTES after an attempt FAILS before it starts the next, so the last attempt
 * ends after every attempt's running time plus the gaps. Two plans, the longer wins: every attempt
 * running a header's worst (a request that failed, then its day split), and two attempts that fail
 * at once followed by a pull of the whole budget. 19 minutes on today's numbers.
 */
const LEAD_MINUTES = Math.max(
  GENERATE_ATTEMPTS * Math.ceil(HEADER_PULL_WORST_SECONDS / 60) +
    (GENERATE_ATTEMPTS - 1) * GENERATE_RETRY_MINUTES,
  (GENERATE_ATTEMPTS - 1) * GENERATE_RETRY_MINUTES + PULL_BUDGET_MINUTES,
)
/**
 * The leads a manual report may choose for its preload (CLI-1005): how long before each send
 * moment its draft is pulled. Fixed choices rather than free minutes. The shortest still lets every
 * attempt finish with a tick to spare before the moment (`auto-send.test.ts`); the longest keeps
 * the pull recent enough to edit from, since a story that lands after it waits for the analyst's
 * Pull.
 */
export const MANUAL_PRELOAD_CHOICES = [30, 60, 90, 120, 180] as const
/**
 * The lead a manual report gets when it chooses none: an hour (Alex, 2026-10-27). CLI-982 started at
 * half an hour (Kaushik, 2026-10-23), and analysts were opening drafts before it: Phoebe 47 and 76
 * minutes ahead on 2026-10-27. Longer only if the build lead outgrows it.
 */
const MANUAL_PRELOAD_MINUTES = Math.max(60, LEAD_MINUTES + TICK_MINUTES)

/**
 * The numbers automatic sends run on (CLI-741, tech sync 2026-09-24), beside the send-time
 * arithmetic because the queue says "builds n minutes before" and the tick does the building, and
 * one guessing the other's number is how they drift.
 *
 * The tick looks CREATE_AHEAD_MINUTES ahead and creates the issue; generation is scheduled to
 * start LEAD_MINUTES before the moment and gets GENERATE_ATTEMPTS tries, GENERATE_RETRY_MINUTES
 * after each failure, each of which may take up to PULL_BUDGET_MINUTES — the arithmetic has to fit
 * inside the lead, which is derived from TOPIC_PULL_TIMING above (`apps/api/src/lib/auto-send.test.ts`
 * pins it). A build that finishes AFTER its moment sends immediately rather than failing, up to
 * LATE_SEND_MAX_MINUTES late; beyond that it is a miss. The portal's equivalents were a 30-minute
 * cron window and a 90-minute preload that nobody had tied together; this is the tie.
 */
export const AUTO_SEND_TIMING = {
  /** How far ahead the tick creates the issue for a moment: half an hour, or a tick past the lead. */
  CREATE_AHEAD_MINUTES: Math.max(30, LEAD_MINUTES + TICK_MINUTES),
  /** How long before the send moment generation starts — derived above. */
  LEAD_MINUTES,
  /** The cron cadence the tick runs on (deploy.yml's Cloud Scheduler job, `headline-auto-send-tick`). */
  TICK_MINUTES,
  GENERATE_ATTEMPTS,
  GENERATE_RETRY_MINUTES,
  PULL_BUDGET_MINUTES,
  /**
   * The longest a build can actually run: Cloud Run's request timeout (deploy.yml's
   * REQUEST_TIMEOUT, pinned by `auto-send.test.ts`). A build attempt's claim older than this
   * belongs to a request that is gone (CLI-982); the pull budget above is a plan, not a limit.
   */
  BUILD_TIMEOUT_MINUTES: 15,
  /**
   * A send this many minutes past its moment is LATE: the tick alerts once (`report.late`) and
   * acts — a scheduled send whose clock never fired goes out now, a draft never built is built
   * now. Kevin, 2026-09-30: the alert should fire after about five minutes, and separately from
   * how long a late brief is still worth sending (below).
   */
  LATE_AFTER_MINUTES: 5,
  /**
   * A send this late still goes out immediately; later than this is a miss, not a late brief.
   * Two hours is a placeholder until CS says how long after the moment a client still wants the
   * brief (Kevin, 2026-09-30) — change it here, nowhere else.
   */
  LATE_SEND_MAX_MINUTES: 120,
  /**
   * An automatic issue with fewer kept items than this is skipped rather than sent — nobody wants
   * an empty briefing. Lowered from the portal's "more than two" to one (tech sync 2026-09-24).
   */
  MIN_ITEMS: 1,
  /**
   * How long before a scheduled MANUAL report's send moment its draft is pulled (CLI-982), so the
   * analyst who opens it finds the coverage already there, when the report chooses no lead of its
   * own — `preloadMinutesOf` is the one place a report's lead is read. The same three attempts as
   * a build, all of them finished before the moment (`auto-send.test.ts`).
   */
  MANUAL_PRELOAD_MINUTES,
  /** The longest lead a report may choose: how far ahead the tick has to look for manual moments. */
  MANUAL_PRELOAD_MAX_MINUTES: Math.max(...MANUAL_PRELOAD_CHOICES),
} as const

/**
 * How long before each send moment this manual report's draft is pulled (CLI-1005): the report's
 * own choice, else the default. The tick, the editor's wait for the pull and the Delivery tab's
 * words all ask here, so they can never disagree about when a draft is ready.
 */
export function preloadMinutesOf(delivery: Pick<Delivery, 'preloadMinutes'>): number {
  return delivery.preloadMinutes ?? AUTO_SEND_TIMING.MANUAL_PRELOAD_MINUTES
}

/**
 * How far ahead of a send moment the tick creates a manual draft with this lead: one tick more
 * than the preload, so the draft exists and its pull is queued before the pull is due, and QStash
 * starts it on the minute.
 */
export function manualCreateAheadMinutes(preloadMinutes: number): number {
  return preloadMinutes + TICK_MINUTES
}

/**
 * When the pull of a manual report's draft for `moment` starts (CLI-1005): the report's lead before
 * the moment, but never before the report's previous moment. Previously-sent is judged when the
 * pull runs, so a draft pulled while the send ahead of it is still a draft holds that send's
 * stories too — on a report that sends at 9:00 and 11:00 on a three-hour lead, the 11:00 draft would
 * carry everything the 9:00 one does. The tick creates the draft a tick before this and queues its
 * pull for it.
 */
export function preloadStartsAt(delivery: Delivery, moment: Date): Date {
  const byLead = moment.getTime() - preloadMinutesOf(delivery) * 60_000
  const previous = previousSendTime(delivery, moment)?.getTime() ?? byLead
  return new Date(Math.max(byLead, previous))
}

/**
 * Whether a draft is waiting on its preload (CLI-982): a manual draft aimed at a send moment, never
 * pulled, inside the window in which the tick makes it and pulls it — `preloadMinutes` is its
 * report's lead (`preloadMinutesOf`). An editor opened before the pull starts watches for it, so
 * the coverage appears without a reload.
 */
export function awaitsPreload(
  issue: {
    origin: string
    status: string
    targetSendAt: string | null
    pulledAt: string | null
  },
  now: Date,
  preloadMinutes: number,
): boolean {
  if (issue.origin !== 'manual' || issue.status !== 'draft') return false
  if (!issue.targetSendAt || issue.pulledAt) return false
  const target = Date.parse(issue.targetSendAt)
  const opens = target - manualCreateAheadMinutes(preloadMinutes) * 60_000
  return now.getTime() >= opens && now.getTime() < target
}
